// src/components/ui/DriverForm.jsx
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { registerDriver } from '../../store/driverSlice';
import { VEHICLE_TYPE, LICENSE_TYPE } from '../../store/driverSlice';
import { 
  validateDriverData, 
  hasErrors,
  getFirstError 
} from '../../utils/driverValidation';
import { Input } from './Input';
import { Select } from './Select';
import { Button } from './Button/Button';
import { useToast } from './Toast';

export function DriverForm({ context = 'admin', onSuccess, onCancel, showHeader = true }) {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      address: '',
      emergencyContact: {
        name: '',
        phone: '',
        relationship: ''
      }
    },
    licenseInfo: {
      licenseNumber: '',
      licenseType: '',
      issueDate: '',
      expiryDate: '',
      issuingState: ''
    },
    vehicleInfo: {
      make: '',
      model: '',
      year: '',
      color: '',
      plateNumber: '',
      vehicleType: '',
      registrationNumber: '',
      insurancePolicyNumber: ''
    },
    bankingInfo: {
      accountHolderName: '',
      accountNumber: '',
      routingNumber: '',
      bankName: ''
    }
  });

  const handleInputChange = (section, field, value) => {
    if (field.includes('.')) {
      const [parentField, childField] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [parentField]: {
            ...prev[section][parentField],
            [childField]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
    
    // Clear error for this field
    if (errors[`${section}.${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`${section}.${field}`]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all data
    const allErrors = validateDriverData(formData);
    setErrors(allErrors);
    
    if (hasErrors(allErrors)) {
      showError('Validation Error', getFirstError(allErrors));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await dispatch(registerDriver(formData));
      
      if (result.success) {
        showSuccess('Success', 'Driver added successfully');
        onSuccess?.();
      } else {
        showError('Error', result.error || 'Failed to add driver');
      }
    } catch (error) {
      showError('Error', 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {showHeader && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Add New Driver</h2>
          <p className="text-sm text-gray-600 mt-1">Enter driver information to create a new account</p>
        </div>
      )}

      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <Input
            name="firstName"
            label="First Name"
            value={formData.personalInfo.firstName}
            onChange={(e) => handleInputChange('personalInfo', 'firstName', e.target.value)}
            error={errors['personalInfo.firstName']}
            required
          />
          <Input
            name="lastName"
            label="Last Name"
            value={formData.personalInfo.lastName}
            onChange={(e) => handleInputChange('personalInfo', 'lastName', e.target.value)}
            error={errors['personalInfo.lastName']}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            name="email"
            type="email"
            label="Email"
            value={formData.personalInfo.email}
            onChange={(e) => handleInputChange('personalInfo', 'email', e.target.value)}
            error={errors['personalInfo.email']}
            required
          />
          <Input
            name="phone"
            label="Phone"
            value={formData.personalInfo.phone}
            onChange={(e) => handleInputChange('personalInfo', 'phone', e.target.value)}
            error={errors['personalInfo.phone']}
            required
          />
        </div>

        <Input
          name="address"
          label="Address"
          value={formData.personalInfo.address}
          onChange={(e) => handleInputChange('personalInfo', 'address', e.target.value)}
          error={errors['personalInfo.address']}
          required
        />
      </div>

      {/* License Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">License Information</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <Input
            name="licenseNumber"
            label="License Number"
            value={formData.licenseInfo.licenseNumber}
            onChange={(e) => handleInputChange('licenseInfo', 'licenseNumber', e.target.value)}
            error={errors['licenseInfo.licenseNumber']}
            required
          />
          <Select
            name="licenseType"
            label="License Type"
            value={formData.licenseInfo.licenseType}
            onChange={(e) => handleInputChange('licenseInfo', 'licenseType', e.target.value)}
            options={Object.values(LICENSE_TYPE).map(type => ({ value: type, label: type.toUpperCase() }))}
            error={errors['licenseInfo.licenseType']}
            required
          />
        </div>
      </div>

      {/* Vehicle Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Vehicle Information</h3>
        
        <div className="grid grid-cols-3 gap-4">
          <Input
            name="make"
            label="Make"
            value={formData.vehicleInfo.make}
            onChange={(e) => handleInputChange('vehicleInfo', 'make', e.target.value)}
            error={errors['vehicleInfo.make']}
            required
          />
          <Input
            name="model"
            label="Model"
            value={formData.vehicleInfo.model}
            onChange={(e) => handleInputChange('vehicleInfo', 'model', e.target.value)}
            error={errors['vehicleInfo.model']}
            required
          />
          <Input
            name="year"
            type="number"
            label="Year"
            value={formData.vehicleInfo.year}
            onChange={(e) => handleInputChange('vehicleInfo', 'year', e.target.value)}
            error={errors['vehicleInfo.year']}
            min="2000"
            max={new Date().getFullYear() + 1}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            name="plateNumber"
            label="Plate Number"
            value={formData.vehicleInfo.plateNumber}
            onChange={(e) => handleInputChange('vehicleInfo', 'plateNumber', e.target.value)}
            error={errors['vehicleInfo.plateNumber']}
            required
          />
          <Select
            name="vehicleType"
            label="Vehicle Type"
            value={formData.vehicleInfo.vehicleType}
            onChange={(e) => handleInputChange('vehicleInfo', 'vehicleType', e.target.value)}
            options={Object.values(VEHICLE_TYPE).map(type => ({ value: type, label: type.charAt(0).toUpperCase() + type.slice(1) }))}
            error={errors['vehicleInfo.vehicleType']}
            required
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Adding Driver...' : 'Add Driver'}
        </Button>
      </div>
    </form>
  );
}
